import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateVision, deleteVision } from '../../actions/planningAction';
import config from '../../config';
import './PlanningSettings.css';

export default function PlanningSettings({ vision, visionId, theme, onUpdated, locked }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [title, setTitle] = useState(vision?.title || '');
  const [timeframe, setTimeframe] = useState(vision?.timeframe || 1);
  const [isPublic, setIsPublic] = useState(!!vision?.isPublic);
  const [publishing, setPublishing] = useState(false);
  const [status, setStatus] = useState(vision?.status || 'active');
  const [statusSaving, setStatusSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [shareState, setShareState] = useState('idle'); // 'idle' | 'copied' | 'shared'

  const dirty = title.trim() !== (vision?.title || '') || timeframe !== (vision?.timeframe || 1);

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await dispatch(updateVision(visionId, { title: title.trim(), timeframe }));
      onUpdated?.();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (_) {}
    setSaving(false);
  };

  const STATUSES = [
    { key: 'standby', label: 'Standby', hint: 'Not started yet. Any activity switches it to Active automatically.' },
    { key: 'active', label: 'Active', hint: 'In progress. You can add, edit, and check things off.' },
    { key: 'reality', label: 'Reality', hint: 'Achieved and locked. No further activity is allowed until you switch it back.' },
  ];

  const changeStatus = async (next) => {
    if (statusSaving || next === status) return;
    const prev = status;
    setStatusSaving(true);
    setStatus(next); // optimistic
    try {
      await dispatch(updateVision(visionId, { status: next }));
      onUpdated?.();
    } catch (_) {
      setStatus(prev);
    }
    setStatusSaving(false);
  };

  const togglePublic = async () => {
    if (publishing) return;
    const next = !isPublic;
    setPublishing(true);
    setIsPublic(next); // optimistic
    try {
      await dispatch(updateVision(visionId, { isPublic: next }));
      onUpdated?.();
    } catch (_) {
      setIsPublic(!next); // revert
    }
    setPublishing(false);
  };

  const shareLink = async () => {
    const shareUrl = `${config.apiUrl}/share/vision/${visionId}`;
    const text = title ? `${title} — on dmyor` : 'My vision on dmyor';
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url: shareUrl });
        setShareState('shared');
      } catch (_) { return; } // user cancelled the native share sheet
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareState('copied');
      } catch (_) { return; }
    }
    setTimeout(() => setShareState('idle'), 2200);
  };

  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await dispatch(deleteVision(visionId));
      navigate('/dashboard/planning', { replace: true });
    } catch (_) {
      setDeleting(false);
    }
  };

  return (
    <div className={`ps-wrap${theme === 'light' ? ' ps-wrap--light' : ''}`}>
      <div className="ps-inner">
        <div className="ps-header">
          <h1 className="ps-heading">Vision settings</h1>
          <p className="ps-sub">Manage this vision: its name, horizon, and removal.</p>
        </div>

        {locked && (
          <div className="ps-locked-note">
            <i className="bx bx-info-circle" />
            This vision is in the Phase 2 preview — planning tools (nodes, execute, habits, docs, journal, chat) unlock soon. Renaming, visibility, and deleting still work below.
          </div>
        )}

        {/* General */}
        <section className="ps-card">
          <div className="ps-card-label">General</div>

          <div className="ps-field">
            <label className="ps-field-label">Vision name</label>
            <input
              className="ps-input"
              value={title}
              maxLength={200}
              onChange={e => setTitle(e.target.value)}
              placeholder="What are you building?"
            />
          </div>

          <div className="ps-field">
            <label className="ps-field-label">Horizon</label>
            <div className="ps-yr-row">
              {[1, 2, 3, 4, 5].map(y => (
                <button
                  key={y}
                  className={`ps-yr-btn${timeframe === y ? ' ps-yr-btn--on' : ''}`}
                  onClick={() => setTimeframe(y)}
                >
                  {y}y
                </button>
              ))}
            </div>
            <p className="ps-hint">
              {timeframe === 5 ? 'PROJECT-5: 5-year maximum ambition horizon.' : `${timeframe}-year plan`}
            </p>
          </div>

          <div className="ps-actions">
            <button className="ps-save-btn" onClick={save} disabled={!dirty || !title.trim() || saving}>
              {saving ? 'Saving…' : savedFlash ? '✓ Saved' : 'Save changes'}
            </button>
          </div>
        </section>

        {/* Status */}
        <section className="ps-card">
          <div className="ps-card-label">Status</div>
          <div className="ps-status-row">
            {STATUSES.map(s => (
              <button
                key={s.key}
                className={`ps-status-btn ps-status-btn--${s.key}${status === s.key ? ' ps-status-btn--on' : ''}`}
                onClick={() => changeStatus(s.key)}
                disabled={statusSaving}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="ps-hint">{STATUSES.find(s => s.key === status)?.hint}</p>
        </section>

        {/* Publish to Explore */}
        <section className="ps-card">
          <div className="ps-card-label">Explore</div>
          <div className="ps-danger-row">
            <div className="ps-danger-text">
              <p className="ps-danger-title">Make this vision public</p>
              <p className="ps-danger-desc">
                Publish it to the Explore feed so anyone can see your vision and its milestones. Your journal, documents, and todos stay private.
              </p>
            </div>
            <button
              className={`ps-toggle${isPublic ? ' ps-toggle--on' : ''}`}
              onClick={togglePublic}
              disabled={publishing}
              role="switch"
              aria-checked={isPublic}
              title={isPublic ? 'Public — click to make private' : 'Private — click to publish'}
            >
              <span className="ps-toggle-knob" />
            </button>
          </div>

          {isPublic && (
            <div className="ps-share-row">
              <div className="ps-danger-text">
                <p className="ps-danger-title">Share link</p>
                <p className="ps-danger-desc">A nicely-formatted page anyone can open, with the vision's name, tenure, and who made it.</p>
              </div>
              <button className="ps-share-btn" onClick={shareLink}>
                {shareState === 'copied' ? 'Link copied ✓' : shareState === 'shared' ? 'Shared ✓' : 'Copy link'}
              </button>
            </div>
          )}
        </section>

        {/* Danger zone */}
        <section className="ps-card ps-card--danger">
          <div className="ps-card-label ps-card-label--danger">Danger zone</div>
          <div className="ps-danger-row">
            <div className="ps-danger-text">
              <p className="ps-danger-title">Delete this vision</p>
              <p className="ps-danger-desc">
                Permanently removes this vision and everything inside it: all milestones, actions, documents, habits, todos, and journal. This cannot be undone.
              </p>
            </div>
            <button className="ps-delete-btn" onClick={() => { setShowDelete(true); setConfirmText(''); }}>
              Delete vision
            </button>
          </div>
        </section>
      </div>

      {showDelete && (
        <div className="ps-modal-overlay" onMouseDown={() => !deleting && setShowDelete(false)}>
          <div className="ps-modal" onMouseDown={e => e.stopPropagation()}>
            <h3 className="ps-modal-title">Delete "{vision?.title || 'this vision'}"?</h3>
            <p className="ps-modal-text">
              This wipes the entire vision and all its contents. There's no undo. Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              className="ps-modal-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div className="ps-modal-actions">
              <button className="ps-modal-cancel" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</button>
              <button
                className="ps-modal-go"
                onClick={remove}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
